import { useMemo, useState } from 'react';
import { Badge } from '@clay-run/glaze/badge';
import { Heading } from '@clay-run/glaze/heading';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { Popover, PopoverTrigger, PopoverPanel } from '@clay-run/glaze/popover';
import { Text } from '@clay-run/glaze/text';
import { formatEventDate, todayIso } from '../dates';
import type { Campaign } from '../../_shared/launcher-campaign.types';
import type { PlaybooksPlaySummary } from '../schemas';
import { subtypeBadgeColor } from '../subtype-badge-colors';
import { PlaysActions } from './plays-actions';

/**
 * Launcher V2 — the calendar month-grid gap component (plan U2, R6).
 *
 * The GRID MATH is a direct port of `LauncherListPage.tsx`'s `MonthCalendar`
 * (Key Decision 3 — "the ported grid *is* investigation E's 'calendar month
 * grid' gap component"), factored into the pure, DOM-free
 * {@link buildMonthGridWeeks} so it's unit-testable under `bun:test` (no DOM
 * harness exists under `mini-apps/` — see `v2-home-view-toggle.test.tsx`).
 * Everything else — the markup, the event chips, the chip popover — is
 * rebuilt on real Glaze components:
 *
 *  - Event chips are a Glaze `Badge` (colored via `subtypeBadgeColor`, R8)
 *    wrapped in a `Popover`/`PopoverTrigger`/`PopoverPanel` (base-ui backed,
 *    verified against `node_modules/@clay-run/glaze/src/popover.tsx`)
 *    instead of v1's hand-positioned `createPortal` card with hardcoded
 *    `bg-nightshade-900 text-white` dark styling — that hardcoded card is
 *    exactly what this rebuild removes.
 *  - The `Badge` sits INSIDE the trigger's children rather than being made
 *    the trigger itself: `Badge` doesn't forward a ref (`badge.tsx` has no
 *    `React.forwardRef` on its static-span branch), so it can't safely be a
 *    Popover trigger's `render` target. `PopoverTrigger` (a real `<button>`,
 *    base-ui's own ref-forwarding primitive) is the interactive element;
 *    `Badge` is decorative content inside it.
 *
 * QA round 7, U1 (item 1) — there is no per-popover fetch any more, so the
 * N+1 this comment used to warn against reintroducing cannot recur: `plays` /
 * `playsLoading` / `playsError` are threaded down from `PlaybooksHome`'s ONE
 * `usePlaysLibrary()` read (`playsByCampaign`), the same batch read the List
 * view and the Library section use. `CalendarChip` renders the same
 * `PlaysActions` ("Open" button per play + always-available "Create Play"
 * Button) that `CampaignPlaysSection` renders in List view, via the shared
 * `plays-actions.tsx` module.
 */

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface MonthGridCell {
  iso: string;
  day: number;
  inMonth: boolean;
}

function toIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Builds a month's calendar grid as full weeks (each exactly 7 cells),
 * padded with the trailing days of the previous month and the leading days
 * of the next month so every week row is complete. `viewMonth` is 0-based
 * (matches `Date`'s own convention). Pure, DOM-free — a direct port of
 * `LauncherListPage.tsx`'s inline grid-building loop.
 */
export function buildMonthGridWeeks(viewYear: number, viewMonth: number): MonthGridCell[][] {
  const firstDay = new Date(viewYear, viewMonth, 1);
  const startDow = firstDay.getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const cells: MonthGridCell[] = [];
  for (let i = 0; i < startDow; i++) {
    const d = new Date(viewYear, viewMonth, -startDow + i + 1);
    cells.push({ iso: toIso(d), day: d.getDate(), inMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ iso: toIso(new Date(viewYear, viewMonth, d)), day: d, inMonth: true });
  }
  while (cells.length % 7 !== 0) {
    const d = new Date(viewYear, viewMonth + 1, cells.length - startDow - daysInMonth + 1);
    cells.push({ iso: toIso(d), day: d.getDate(), inMonth: false });
  }

  const weeks: MonthGridCell[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return weeks;
}

interface CalendarChipProps {
  campaign: Campaign;
  plays: PlaybooksPlaySummary[];
  playsLoading: boolean;
  playsError: boolean;
  playbookNameById: Map<string, string>;
  basePath: string;
  onCreatePlay: (campaign: Campaign) => void;
  creatingPlay: boolean;
  onOpen: (href: string) => void;
}

function CalendarChip({
  campaign,
  plays,
  playsLoading,
  playsError,
  playbookNameById,
  basePath,
  onCreatePlay,
  creatingPlay,
  onOpen,
}: CalendarChipProps) {
  const hue = subtypeBadgeColor(campaign.subtype);
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger className="block w-full min-w-0 rounded-sm text-left focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-outline-focus-ring">
        <Badge color={hue} className="w-full">
          {campaign.name}
        </Badge>
      </PopoverTrigger>
      <PopoverPanel positioner={{ side: 'bottom', align: 'start' }}>
        <div className="flex w-56 flex-col gap-2 p-2">
          <Text size="sm" weight="semibold">
            {campaign.name}
          </Text>
          <Text size="xs" variant="tertiary">
            {formatEventDate(campaign.event_date) ?? 'No date'}
            {campaign.subtype ? ` · ${campaign.subtype}` : ''}
          </Text>
          <Text size="xs" variant="tertiary">
            {campaign.member_count.toLocaleString()} members
          </Text>
          {playsLoading && (
            <div className="flex items-center gap-2">
              <LoadingSpinner size="xs" />
              <Text size="xs" variant="tertiary">Loading plays…</Text>
            </div>
          )}
          {playsError && (
            <Text size="xs" variant="critical">Couldn't load plays for this campaign.</Text>
          )}
          {!playsLoading && !playsError && (
            <PlaysActions
              plays={plays}
              campaign={campaign}
              playbookNameById={playbookNameById}
              basePath={basePath}
              onCreatePlay={onCreatePlay}
              creatingPlay={creatingPlay}
              onOpen={onOpen}
            />
          )}
        </div>
      </PopoverPanel>
    </Popover>
  );
}

export interface CalendarMonthGridProps {
  campaigns: Campaign[];
  /** 0-based, matches `Date`'s own convention. */
  viewYear: number;
  viewMonth: number;
  playbookNameById: Map<string, string>;
  /** QA round 7, U1 (item 1) — from `PlaybooksHome`'s ONE `usePlaysLibrary()` read (`playsByCampaign`), never fetched here. */
  playsByCampaignId: Map<string, PlaybooksPlaySummary[]>;
  playsLoading: boolean;
  playsError: boolean;
  basePath: string;
  onCreatePlay: (campaign: Campaign) => void;
  creatingPlayId: string | null;
  onOpen: (href: string) => void;
}

export function CalendarMonthGrid({
  campaigns,
  viewYear,
  viewMonth,
  playbookNameById,
  playsByCampaignId,
  playsLoading,
  playsError,
  basePath,
  onCreatePlay,
  creatingPlayId,
  onOpen,
}: CalendarMonthGridProps) {
  const today = todayIso();
  const weeks = useMemo(() => buildMonthGridWeeks(viewYear, viewMonth), [viewYear, viewMonth]);

  const byDate = useMemo(() => {
    const map = new Map<string, Campaign[]>();
    for (const c of campaigns) {
      if (!c.event_date) continue;
      const existing = map.get(c.event_date) ?? [];
      existing.push(c);
      map.set(c.event_date, existing);
    }
    return map;
  }, [campaigns]);

  return (
    <div className="overflow-hidden rounded-lg border border-border-primary bg-bg-primary">
      <div className="px-6 pt-5 pb-4">
        <Heading size="sm">
          {MONTH_NAMES[viewMonth]}{' '}
          <span className="ml-1 text-xl font-normal text-content-tertiary">{viewYear}</span>
        </Heading>
      </div>

      <div className="grid grid-cols-7 border-t border-border-primary">
        {DOW_LABELS.map((d, i) => (
          <div
            key={d}
            className={`border-b border-border-primary py-2 text-center font-mono text-[11px] font-medium tracking-wider uppercase ${
              i >= 5 ? 'text-content-disabled' : 'text-content-tertiary'
            }`}
          >
            {d}
          </div>
        ))}
      </div>

      {weeks.map((week) => (
        <div key={week[0].iso} className="grid grid-cols-7">
          {week.map(({ iso, day, inMonth }) => {
            const isToday = iso === today;
            const dayCampaigns = byDate.get(iso) ?? [];
            return (
              <div
                key={iso}
                className={`relative min-h-[120px] border-r border-b border-border-secondary p-1.5 last:border-r-0 ${
                  !inMonth ? 'bg-bg-secondary/50 opacity-40' : ''
                }`}
              >
                <span
                  className={`inline-flex h-6 w-6 items-center justify-center text-sm font-medium ${
                    isToday ? 'rounded-full bg-content-primary text-content-inverse' : 'text-content-primary'
                  }`}
                >
                  {day}
                </span>
                <div className="mt-1 flex flex-col gap-1">
                  {dayCampaigns.map((c) => (
                    <CalendarChip
                      key={c.id}
                      campaign={c}
                      plays={playsByCampaignId.get(c.id) ?? []}
                      playsLoading={playsLoading}
                      playsError={playsError}
                      playbookNameById={playbookNameById}
                      basePath={basePath}
                      onCreatePlay={onCreatePlay}
                      creatingPlay={creatingPlayId === c.id}
                      onOpen={onOpen}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
